import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { needsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const positions = [
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const types = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'both', label: 'Ambos (Transferencia o Préstamo)' },
];

const priorities = [
  { value: 'high', label: 'Alta - Necesidad urgente' },
  { value: 'medium', label: 'Media - Para esta temporada' },
  { value: 'low', label: 'Baja - A futuro' },
];

export const AddNeedPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    position: '',
    type: '',
    priority: '',
    min_age: '',
    max_age: '',
    preferred_nationalities: '',
    budget: '',
    max_salary: '',
    description: '',
    deadline: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.position || !formData.type || !formData.priority) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        min_age: formData.min_age ? parseInt(formData.min_age) : null,
        max_age: formData.max_age ? parseInt(formData.max_age) : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        max_salary: formData.max_salary ? parseFloat(formData.max_salary) : null,
        preferred_nationalities: formData.preferred_nationalities 
          ? formData.preferred_nationalities.split(',').map(n => n.trim()) 
          : null,
      };
      
      await needsAPI.create(data);
      toast.success('Necesidad publicada correctamente');
      navigate('/my-publications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar la necesidad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="add-need-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
          <h1 className="font-heading text-2xl font-bold text-slate-800 uppercase tracking-tight mb-6">
            Publicar Necesidad de Fichaje
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Required fields */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Información Básica *
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Posición buscada *</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData({ ...formData, position: value })}
                    required
                  >
                    <SelectTrigger data-testid="need-position">
                      <SelectValue placeholder="Seleccionar posición" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Tipo de operación *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                    required
                  >
                    <SelectTrigger data-testid="need-type">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {types.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Prioridad *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    required
                  >
                    <SelectTrigger data-testid="need-priority">
                      <SelectValue placeholder="Seleccionar prioridad" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Optional fields */}
            <div className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-2">
                Requisitos (Opcional)
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Edad mínima</Label>
                  <Input
                    type="number"
                    value={formData.min_age}
                    onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
                    placeholder="18"
                    min="15"
                    max="50"
                    data-testid="need-min-age"
                  />
                </div>

                <div>
                  <Label className="form-label">Edad máxima</Label>
                  <Input
                    type="number"
                    value={formData.max_age}
                    onChange={(e) => setFormData({ ...formData, max_age: e.target.value })}
                    placeholder="30"
                    min="15"
                    max="50"
                    data-testid="need-max-age"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Nacionalidades preferidas (separadas por coma)</Label>
                  <Input
                    value={formData.preferred_nationalities}
                    onChange={(e) => setFormData({ ...formData, preferred_nationalities: e.target.value })}
                    placeholder="España, Argentina, Brasil"
                    data-testid="need-nationalities"
                  />
                </div>

                <div>
                  <Label className="form-label">Presupuesto máximo (€)</Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="100000"
                    data-testid="need-budget"
                  />
                </div>

                <div>
                  <Label className="form-label">Salario máximo (€/año)</Label>
                  <Input
                    type="number"
                    value={formData.max_salary}
                    onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                    placeholder="50000"
                    data-testid="need-salary"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Fecha límite</Label>
                  <Input
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    placeholder="31 de enero 2025"
                    data-testid="need-deadline"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="form-label">Descripción / Notas adicionales</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Perfil específico que buscamos, características deseadas..."
                    rows={4}
                    data-testid="need-description"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="submit-need"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Publicar Necesidad
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddNeedPage;
